import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Loader2, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";

import {
  api,
  type ConfigSnapshot,
  type ConfigScriptSnapshot,
  type ControllerCompilePreview,
  type ControllerDetail,
  type ControllerMeter,
  type ControllerMeterUpsertRequest,
  type Feeder,
} from "@/lib/api";
import { GrafanaEmbedFrame } from "@/components/grafana-embed";
import { useDialogDraft } from "@/components/dialog-draft";
import { GRAFANA_EMBED_QUERY_OPTIONS } from "@/lib/grafana-embed-query";
import { RETENTION_HELP } from "@/lib/dashboard-time";
import { ControllerDiagnosticsPanel } from "@/pages/diagnostics";
import { ControllerSharingPanel } from "@/components/controller-sharing";
import { RatioVerification } from "@/components/ratio-verification";
import { useAuth } from "@/auth/auth-provider";
import { hasCapability } from "@/auth/capabilities";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/page-header";
import { SearchableSelector } from "@/components/searchable-selector";
import { feederPickerOption } from "@/components/feeder-picker-option";
import { HelpTooltip } from "@/components/help-tooltip";
import { StatusPill } from "@/components/status-pill";
import { formatControllerName, formatDeviceId, formatTimestamp } from "@/lib/format";
import { DEFAULT_NTP_SERVER } from "@/lib/controller-defaults";

type SettingsDraft = {
  display_name: string;
  site_id: string;
  ntp_server: string;
  script_path: string;
};

const FIRMWARE_POLL_INTERVAL_MS = 15000;

type ConfigDeliveryProgress = {
  label: string;
  phase: "draft" | "queued" | "published" | "confirmed" | "attention" | "failed" | "ahead" | "unknown";
  variant: "muted" | "outline" | "secondary" | "success" | "warning" | "destructive";
};

function configDeliveryProgress(controller: ControllerDetail): ConfigDeliveryProgress {
  const desired: number | null = controller.desired_config_id;
  const applied: number | null = controller.reported_config_id;
  if (desired === null) return { label: "No cloud config queued", phase: "draft", variant: "muted" };
  if (applied !== null && applied > desired) {
    return { label: `Device has newer config ${applied}`, phase: "ahead", variant: "warning" };
  }
  if (controller.reported_state === "APPLIED" && applied === desired) {
    return { label: `Device reports config ${desired} applied`, phase: "confirmed", variant: "success" };
  }
  if (controller.desired_publish_status === "pending" || controller.desired_publish_status === "reserved") {
    return { label: `Config ${desired} queued`, phase: "queued", variant: "warning" };
  }
  if (controller.desired_publish_status === "failed") {
    return { label: `Config ${desired} cloud publication failed`, phase: "failed", variant: "destructive" };
  }
  if (controller.desired_publish_status === "published") {
    const reportReceived: number = Date.parse(controller.reported_at ?? "");
    const targetSaved: number = Date.parse(controller.desired_updated_at ?? "");
    const rejectionAfterTarget: boolean = controller.reported_state === "REJECTED"
      && applied !== null && applied < desired
      && Number.isFinite(reportReceived) && Number.isFinite(targetSaved)
      && reportReceived >= targetSaved;
    return rejectionAfterTarget
      ? { label: "Device reports rejection", phase: "attention", variant: "destructive" }
      : { label: `Config ${desired} published; waiting for device`, phase: "published", variant: "secondary" };
  }
  return { label: `Config ${desired} cloud status unknown`, phase: "unknown", variant: "outline" };
}

type MeterDraft = {
  controller_meter_id: number | null;
  slot: string;
  modbus_id: string;
  enabled: boolean;
  label: string;
  source_mode: "feeder" | "custom";
  feeder_id: string;
  custom_ct_primary: string;
  custom_ct_secondary: string;
  custom_vt_primary: string;
  custom_vt_secondary: string;
};

const emptyMeterDraft: MeterDraft = {
  controller_meter_id: null,
  slot: "1",
  modbus_id: "1",
  enabled: true,
  label: "",
  source_mode: "feeder",
  feeder_id: "",
  custom_ct_primary: "",
  custom_ct_secondary: "",
  custom_vt_primary: "",
  custom_vt_secondary: "",
};

export function ControllerDetailPage(): JSX.Element {
  const { deviceId = "" } = useParams<{ deviceId: string }>();
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [meterDraft, setMeterDraft] = useState<MeterDraft | null>(null);
  const [deleteMeter, setDeleteMeter] = useState<ControllerMeter | null>(null);
  const [previewRevision, setPreviewRevision] = useState(0);

  async function clearVerificationPreviews(): Promise<void> {
    const queryKey = ["controller-config-preview", deviceId];
    await queryClient.cancelQueries({ queryKey, exact: true });
    queryClient.removeQueries({ queryKey, exact: true });
    // Discard completed and in-flight draft mutation results, but keep the
    // settings draft owned by this page.
    setPreviewRevision((revision) => revision + 1);
  }

  const controllerQuery = useQuery({
    queryKey: ["controller", deviceId],
    queryFn: () => api.controller(deviceId),
    enabled: Boolean(deviceId),
  });
  const feedersQuery = useQuery({ queryKey: ["feeders"], queryFn: () => api.feeders(), enabled: hasCapability(me, "controllers:read") });
  const grafanaEmbedQuery = useQuery({
    queryKey: ["grafana-embed", "controller-detail", deviceId],
    queryFn: () => api.grafanaControllerEmbed(deviceId),
    enabled: Boolean(deviceId),
    ...GRAFANA_EMBED_QUERY_OPTIONS,
  });
  const controller = controllerQuery.data;
  const feeders = feedersQuery.data ?? [];
  const canManageShares = Boolean(
    controller && (
      hasCapability(me, "controllers:share") &&
      (hasCapability(me, "catalog:write") || me?.tenant_id === controller.tenant_id)
    ),
  );
  const canEdit = Boolean(controller && hasCapability(me, "controllers:configure") &&
    (hasCapability(me, "catalog:write") || me?.tenant_id === controller.tenant_id));

  const [editedSettings, setEditedSettings] = useState<{
    deviceId: string;
    draft: SettingsDraft | null;
  }>({ deviceId, draft: null });
  const settingsDraft: SettingsDraft = editedSettings.deviceId === deviceId
    && editedSettings.draft !== null
    ? editedSettings.draft
    : settingsDraftFromController(controller ?? null);
  const setSettingsDraft = (draft: SettingsDraft): void => {
    setEditedSettings({ deviceId, draft });
  };

  const refreshMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await controllerQuery.refetch({ throwOnError: true });
      const relatedQueryKeys: string[][] = [
        ["controller-config-preview", deviceId],
        ["controller-telemetry", deviceId],
        ["grafana-embed", "controller-detail", deviceId],
        ["grafana-embed", "device-diagnostics", deviceId],
      ];
      await Promise.all(relatedQueryKeys.map((queryKey) =>
        queryClient.refetchQueries(
          { queryKey, exact: true, type: "active" },
          { throwOnError: true },
        ),
      ));
    },
  });

  const saveConfigMutation = useMutation({
    mutationFn: () => {
      if (!controller) throw new Error("controller is not loaded");
      return api
        .updateControllerSettings(controller.device_id, {
          display_name: settingsDraft.display_name,
          site_id: settingsDraft.site_id,
          ntp_server: settingsDraft.ntp_server,
          script_path: settingsDraft.script_path,
        })
        .then(() => api.queueControllerDesired(controller.device_id).catch((error: unknown) => {
          void queryClient.invalidateQueries({ queryKey: ["controller", deviceId] });
          void queryClient.invalidateQueries({ queryKey: ["controller-config-preview", deviceId] });
          const detail: string = (error instanceof Error ? error.message : "Unknown error").replace(/\.$/, "");
          throw new Error(`Cloud settings were saved, but queue confirmation failed: ${detail}. Refresh to check the cloud target before retrying.`);
        }));
    },
    onSuccess: () => {
      setEditedSettings({ deviceId, draft: null });
      void queryClient.invalidateQueries({ queryKey: ["controller", deviceId] });
      void queryClient.invalidateQueries({ queryKey: ["controller-config-preview", deviceId] });
      toast({ title: "Desired config queued", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Config update needs attention",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      }),
  });

  const meterMutation = useMutation({
    mutationFn: (draft: MeterDraft) => {
      if (!controller) throw new Error("controller is not loaded");
      const body = meterRequestFromDraft(draft);
      if (draft.controller_meter_id === null) {
        return api.createControllerMeter(controller.device_id, body);
      }
      return api.updateControllerMeter(controller.device_id, draft.controller_meter_id, body);
    },
    onSuccess: () => {
      setMeterDraft(null);
      void queryClient.invalidateQueries({ queryKey: ["controller", deviceId] });
      void queryClient.invalidateQueries({ queryKey: ["controller-config-preview", deviceId] });
      toast({ title: "Meter assignment saved", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Meter save failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (meter: ControllerMeter) => {
      if (!controller) throw new Error("controller is not loaded");
      return api.deleteControllerMeter(controller.device_id, meter.controller_meter_id);
    },
    onSuccess: () => {
      setDeleteMeter(null);
      void queryClient.invalidateQueries({ queryKey: ["controller", deviceId] });
      void queryClient.invalidateQueries({ queryKey: ["controller-config-preview", deviceId] });
      toast({ title: "Meter assignment deleted", variant: "success" });
    },
    onError: (error: unknown) =>
      toast({
        title: "Meter delete failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      }),
  });

  if (controllerQuery.error && !controller) {
    return (
      <>
        <PageHeader title="Controller" />
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {controllerQuery.error instanceof Error
            ? controllerQuery.error.message
            : "Failed to load controller"}
        </div>
      </>
    );
  }

  if (controllerQuery.isLoading || !controller) {
    return (
      <>
        <PageHeader title="Loading controller..." />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, idx) => (
            <Skeleton key={idx} className="h-28" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={formatControllerName(controller.display_name, controller.serial_alias)}
        description={`${formatDeviceId(controller.device_id)} · ${controller.tenant_name ?? "Unassigned"} · ${controller.site_id || "No site"}`}
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/controllers">
                <ArrowLeft className="h-4 w-4" /> Back to controllers
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={refreshMutation.isPending}
              onClick={() => refreshMutation.mutate()}
            >
              {refreshMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {refreshMutation.isPending ? "Refreshing..." : "Refresh cloud data"}
            </Button>
            <HelpTooltip label="About cloud refresh">
              Refresh reloads cloud data. It does not request a new device config report.
            </HelpTooltip>
          </>
        }
      />

      <div className="text-xs text-muted-foreground">
        {refreshMutation.isPending ? <p role="status">Refreshing cloud data...</p> : null}
        {refreshMutation.isSuccess ? <p role="status">Cloud data refreshed.</p> : null}
        {refreshMutation.isError ? (
          <p role="alert" className="text-destructive">
            Cloud refresh failed: {refreshMutation.error instanceof Error ? refreshMutation.error.message : "Unknown error"}
          </p>
        ) : null}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <StatusPill status={controller.connection_status} />
        {controller.reported_state === "REJECTED" ? (
          <HelpTooltip label="About device rejection" className="h-auto rounded-md bg-destructive px-2 py-0.5 text-xs font-semibold text-destructive-foreground hover:bg-destructive/90"
            trigger={<span>Last device report: REJECTED</span>}>
            <span className="block font-semibold">Device rejected a configuration</span>
            <span className="mt-2 block">{controller.reported_error || "The device did not report error details."}</span>
            <span className="block">Failure reason: {controller.reported_failure_reason || "not reported"}</span>
            <span className="block">Failure count: {controller.reported_failure_count ?? "unknown"}</span>
            <span className="block">Rejection report received at: {controller.reported_at ? formatTimestamp(controller.reported_at) : "unknown"}</span>
            <span className="mt-2 block">The last good applied ID does not identify the rejected attempt. The desired ID is the cloud target.</span>
          </HelpTooltip>
        ) : <Badge variant="muted">
          Last device report: {controller.reported_state ?? "none"}
        </Badge>}
        <Badge variant="muted">Desired: {controller.desired_config_id ?? "none"}</Badge>
        <Badge variant="muted">Last good applied ID: {controller.reported_config_id ?? "none"}</Badge>
        <Badge role="status" aria-label="Configuration delivery status" variant={configDeliveryProgress(controller).variant}
          className={configDeliveryProgress(controller).phase === "confirmed" ? "bg-emerald-600 text-white" : undefined}>
          {configDeliveryProgress(controller).label}
        </Badge>
        <span className="text-xs text-muted-foreground">
          Last telemetry {formatTimestamp(controller.latest_telemetry_at)}
        </span>
      </div>

      <Tabs defaultValue="telemetry" className="mt-3">
        <TabsList>
          <TabsTrigger value="telemetry">Telemetry</TabsTrigger>
          <TabsTrigger value="config">Config</TabsTrigger>
          <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
          {canManageShares ? <TabsTrigger value="sharing">Sharing</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="telemetry" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Meter drill-down</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {(controller.reported_config?.berry?.script_config?.m ?? []).map(([modbusId]) => {
                  const meterId = String(modbusId);
                  return (
                    <Button key={meterId} size="sm" variant="outline" asChild>
                      <Link
                        to={`/controllers/${encodeURIComponent(controller.device_id)}/meters/${encodeURIComponent(meterId)}`}
                      >
                        {meterLabel(controller, meterId)} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  );
                })}
              </div>
              {controller.reported_config?.berry?.script_config?.m === undefined ? (
                <p className="text-sm text-muted-foreground">Device-reported meters are unknown.</p>
              ) : null}
              <p className="mt-3 text-xs text-muted-foreground">
                Saved cloud meters remain available in the Config tab. A meter appears here after the device reports it in its configuration.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Grafana shows the controller aggregate and meter series for the selected time range.
                Open a meter to inspect its raw Modbus stream.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">{RETENTION_HELP}</p>
            </CardContent>
          </Card>
          {grafanaEmbedQuery.error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {grafanaEmbedQuery.error instanceof Error
                ? grafanaEmbedQuery.error.message
                : "Failed to load the Grafana dashboard"}
            </div>
          ) : null}
          {!grafanaEmbedQuery.error && !grafanaEmbedQuery.isFetchedAfterMount ? (
            <Skeleton className="h-[calc(100vh-12rem)] min-h-[720px] w-full" />
          ) : !grafanaEmbedQuery.error && grafanaEmbedQuery.isFetchedAfterMount && grafanaEmbedQuery.data ? (
            <GrafanaEmbedFrame
              url={grafanaEmbedQuery.data.url}
              title="Controller telemetry dashboard"
              onRetry={() => void grafanaEmbedQuery.refetch()}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="config" className="space-y-4">
          <ControllerConfigWorkspace
            key={`${controller.device_id}:${previewRevision}`}
            controller={controller}
            deviceId={controller.device_id}
            settingsDraft={settingsDraft}
            editor={
              <ControllerSettingsCard
                draft={settingsDraft}
                setDraft={setSettingsDraft}
                canEdit={canEdit}
                saving={saveConfigMutation.isPending}
                onSave={() => saveConfigMutation.mutate()}
              />
            }
            meters={
              <ControllerMetersList
                controller={controller}
                canEdit={canEdit}
                feeders={feeders}
                onVerificationChanged={clearVerificationPreviews}
                onCreate={() => setMeterDraft({
                  ...emptyMeterDraft,
                  slot: String(nextSlot(controller)),
                  modbus_id: String(nextSlot(controller)),
                  feeder_id: feeders[0] ? String(feeders[0].feeder_id) : "",
                })}
                onEdit={(meter) => setMeterDraft(draftFromMeter(meter))}
                onDelete={setDeleteMeter}
              />
            }
          />
        </TabsContent>

        <TabsContent value="diagnostics" className="space-y-4">
          <ControllerDiagnosticsPanel deviceId={controller.device_id} />
        </TabsContent>

        {canManageShares ? (
          <TabsContent value="sharing" className="space-y-4">
            <ControllerSharingPanel deviceId={controller.device_id} />
          </TabsContent>
        ) : null}
      </Tabs>

      <MeterDialog
        draft={meterDraft}
        feeders={feeders}
        submitting={meterMutation.isPending}
        onOpenChange={(open) => !open && setMeterDraft(null)}
        setDraft={setMeterDraft}
        onSubmit={() => meterDraft && meterMutation.mutate(meterDraft)}
      />

      <Dialog open={deleteMeter !== null} onOpenChange={(open) => !open && setDeleteMeter(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete meter assignment?</DialogTitle>
            <DialogDescription>
              This removes {deleteMeter?.label || `meter ${deleteMeter?.modbus_id}`} from the
              controller authoring model.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMeter(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMeter && deleteMutation.mutate(deleteMeter)}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MeterRatioStatus({ meter, feeders }: { meter: ControllerMeter; feeders: Feeder[] }): JSX.Element {
  const feeder = feeders.find((candidate) => candidate.feeder_id === meter.feeder_id);
  const source = meter.source_mode === "feeder" ? feeder : meter;
  const stale = meter.source_mode === "feeder" && (!feeder ||
    meter.resolved_ct_primary !== feeder.ct_primary || meter.resolved_ct_secondary !== feeder.ct_secondary ||
    meter.resolved_vt_primary !== feeder.vt_primary || meter.resolved_vt_secondary !== feeder.vt_secondary);
  return (
    <div className="mt-1 font-sans text-muted-foreground">
      {(["ct", "vt"] as const).map((ratio) => {
        const matches = meter.source_mode !== "feeder" || Boolean(feeder &&
          meter[`resolved_${ratio}_primary`] === feeder[`${ratio}_primary`] &&
          meter[`resolved_${ratio}_secondary`] === feeder[`${ratio}_secondary`]);
        return <div key={ratio}>
          {ratio.toUpperCase()} saved ratio: {matches ? source?.[`${ratio}_verification_state`] ?? "unverified" : "unverified"}
          {meter.source_mode === "feeder" && feeder ? <span>
            {" · "}Current feeder {ratio.toUpperCase()} {feeder[`${ratio}_primary`]}:{feeder[`${ratio}_secondary`]}:
            {" "}{feeder[`${ratio}_verification_state`] ?? "unverified"}
          </span> : null}
          {source?.[`${ratio}_verification_source`] ? <p>
            {meter.source_mode === "feeder" ? "Current feeder" : "Custom ratio"} confirmation source: {source[`${ratio}_verification_source`]}
          </p> : null}
        </div>;
      })}
      {stale ? <p>Saved feeder ratios differ from the current feeder or the feeder is unavailable.
        Compiling and queueing use the saved ratio snapshot. Save the meter to refresh it if intended.</p> : null}
    </div>
  );
}

function ControllerMetersList({
  controller,
  canEdit,
  feeders,
  onCreate,
  onEdit,
  onDelete,
  onVerificationChanged,
}: {
  controller: ControllerDetail;
  canEdit: boolean;
  feeders: Feeder[];
  onCreate: () => void;
  onEdit: (meter: ControllerMeter) => void;
  onDelete: (meter: ControllerMeter) => void;
  onVerificationChanged: () => Promise<void>;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 p-3">
        <CardTitle className="text-sm">Controller meters</CardTitle>
        {canEdit ? (
          <Button size="sm" onClick={onCreate}>
            <Plus className="h-4 w-4" /> Add meter
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="p-3 pt-0 xl:max-h-[calc(100vh-16rem)] xl:overflow-y-auto">
        {controller.meters.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No meters assigned to this controller yet.</p>
        ) : (
          <ul aria-label="Controller meter list" className="space-y-3">
            {controller.meters.map((meter) => (
              <li key={meter.controller_meter_id} className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/controllers/${encodeURIComponent(controller.device_id)}/meters/${encodeURIComponent(String(meter.modbus_id))}`}
                      className="break-words text-sm font-semibold text-foreground hover:text-primary"
                    >
                      {meter.label || `Meter ${meter.modbus_id}`}
                    </Link>
                    <div className="font-mono text-xs text-muted-foreground">
                      slot {meter.slot} · modbus {meter.modbus_id}
                    </div>
                  </div>
                  <Badge variant={meter.enabled ? "success" : "muted"}>
                    {meter.enabled ? "enabled" : "disabled"}
                  </Badge>
                </div>
                <div className="min-w-0 text-xs">
                  <Badge variant={meter.source_mode === "feeder" ? "secondary" : "outline"}>
                    {meter.source_mode}
                  </Badge>
                  {meter.feeder_id !== null ? (
                    <div className="mt-1 break-words text-muted-foreground">
                      feeder {feederName(feeders, meter.feeder_id)}
                    </div>
                  ) : null}
                </div>
                <div className="font-mono text-xs">
                  CT {meterCtPrimary(meter)}:{meterCtSecondary(meter)} VT {meterVtPrimary(meter)}:
                  {meterVtSecondary(meter)}
                  <MeterRatioStatus meter={meter} feeders={feeders} />
                  {canEdit && meter.source_mode === "custom" ? <RatioVerification values={meter}
                    ct={[meter.custom_ct_primary ?? 1, meter.custom_ct_secondary ?? 1]}
                    vt={[meter.custom_vt_primary ?? 1, meter.custom_vt_secondary ?? 1]}
                    onVerified={onVerificationChanged}
                    onConfirm={(body) => api.verifyCustomMeterRatio(controller.device_id, meter.controller_meter_id, body)} /> : null}
                </div>
                <div className="flex flex-wrap gap-2 border-t border-border pt-2">
                  {canEdit ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => onEdit(meter)}>
                        <Pencil className="h-4 w-4" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => onDelete(meter)}>
                        <Trash2 className="h-4 w-4" /> Delete
                      </Button>
                    </>
                  ) : <span className="text-xs text-muted-foreground">Read only</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

const configComparisonFields: readonly {
  label: string;
  value: (snapshot: ConfigSnapshot) => string | undefined;
  draftPolicy?: string;
}[] = [
  { label: "Site ID", value: (snapshot) => configText(snapshot.site?.site_id) },
  { label: "NTP server", value: (snapshot) => configText(snapshot.time?.ntp_server) },
  { label: "Berry script path", value: (snapshot) => snapshot.berry?.script_path === undefined
    ? undefined : snapshot.berry.script_path || "Built-in script (empty path)" },
  { label: "Meters", value: (snapshot) => configMeters(snapshot.berry?.script_config?.m) },
  { label: "Primary APN", value: (snapshot) => configText(snapshot.cellular?.apn),
    draftPolicy: "Not sent (device-local)" },
  { label: "Secondary APN", value: (snapshot) => configText(snapshot.cellular?.sim2_apn),
    draftPolicy: "Not sent (device-local)" },
  { label: "poll_ms script key", value: (snapshot) => snapshot.berry?.script_config?.poll_ms === undefined
    ? undefined : `${snapshot.berry.script_config.poll_ms} ms`,
    draftPolicy: "Not sent (firmware default: 15 seconds)" },
  { label: "sentinel_poll_ms script key", value: (snapshot) => snapshot.berry?.script_config?.sentinel_poll_ms === undefined
    ? undefined : `${snapshot.berry.script_config.sentinel_poll_ms} ms`,
    draftPolicy: "Not sent (firmware default: 15 seconds)" },
];

function configText(value: string | undefined): string | undefined {
  return value === undefined ? undefined : value || "Empty";
}

function configMeters(meters: ConfigScriptSnapshot["m"]): string | undefined {
  if (meters === undefined) return undefined;
  return meters.length ? meters.map(([id, ratios]) =>
    `Meter ${id}: CT ${ratios.cp ?? "unknown"}:${ratios.cs ?? "unknown"}, VT ${ratios.vp ?? "unknown"}:${ratios.vs ?? "unknown"}`,
  ).join("\n") : "Empty meter list";
}

function previewConfigSnapshot(preview: ControllerCompilePreview | undefined): ConfigSnapshot | null {
  const value: unknown = preview?.device_shape.config;
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const config: ConfigSnapshot = value as ConfigSnapshot;
  const scriptConfig: unknown = config.berry?.script_config;
  if (typeof scriptConfig !== "string") return config;
  try {
    const parsed: unknown = JSON.parse(scriptConfig);
    return {
      ...config,
      berry: {
        ...config.berry,
        script_config: typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
          ? parsed as ConfigScriptSnapshot : undefined,
      },
    };
  } catch {
    return { ...config, berry: { ...config.berry, script_config: undefined } };
  }
}

function ConfigSnapshotPanel({ controller, source }: {
  controller: ControllerDetail;
  source: "device" | "cloud";
}): JSX.Element {
  const isDevice: boolean = source === "device";
  const snapshot: ConfigSnapshot | null = (isDevice ? controller.reported_config : controller.desired_config) ?? null;
  const configId: number | null = isDevice ? controller.reported_config_id : controller.desired_config_id;
  const timestamp: string | null = (isDevice ? controller.reported_at : controller.desired_updated_at) ?? null;
  const unavailable: boolean = snapshot === null && configId !== null;
  const unsafeDesired: boolean = !isDevice && (snapshot?.cellular !== undefined
    || snapshot?.berry?.script_config?.poll_ms !== undefined
    || snapshot?.berry?.script_config?.sentinel_poll_ms !== undefined);
  return (
    <Card role="region" aria-label={isDevice ? "Last device report" : "Persisted cloud desired"}
      className={isDevice ? "min-w-0 border-secondary/40 bg-secondary/10" : "min-w-0 border-amber-500/40 bg-muted/40"}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3">
        <CardTitle className="text-sm">{isDevice ? "Device config" : "Cloud target"}</CardTitle>
        <HelpTooltip label={isDevice ? "About device reports" : "About cloud target"}>
          {isDevice ? <>
            <span className="block">A device report can be partial or stale. Missing values are not known device settings.</span>
            <span className="mt-2 block">Last reported values. Local changes appear after the device sends a new config report.</span>
            <span className="mt-2 block">Report timestamps are cloud receipt times. Applied ID and state do not prove full payload equality.</span>
          </> : "This is the persisted desired payload, not a saved-settings preview. Published means cloud publication, not device delivery or application."}
        </HelpTooltip>
      </CardHeader>
      <CardContent className="space-y-3 p-3 pt-0 text-xs">
        <div className="space-y-1">
          <p className="font-semibold">{isDevice ? "Applied ID" : "Desired ID"}: {configId ?? "none"}</p>
          <p className="text-muted-foreground">{isDevice ? "Report received at" : "Persisted"}: {timestamp ? formatTimestamp(timestamp) : "unknown"}</p>
          {!isDevice ? <>
            <p>Publication: {controller.desired_publish_status ?? "unknown"}</p>
            <p className="text-muted-foreground">Published: {controller.desired_published_at ? formatTimestamp(controller.desired_published_at) : "unknown"}</p>
          </> : null}
        </div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 border-t border-border pt-3">
          {configComparisonFields.slice(0, 4).map((field) => (
            <div key={field.label} className="contents">
              <dt className="text-muted-foreground">{field.label}</dt>
              <dd className="min-w-0 break-words">{unavailable ? "Snapshot unavailable"
                : field.label === "Meters" && snapshot?.berry?.script_config?.m !== undefined
                  ? `${snapshot.berry.script_config.m.length} configured`
                  : field.value(snapshot ?? {}) ?? (isDevice ? "Not reported" : "Not sent")}</dd>
            </div>
          ))}
        </dl>
        {unsafeDesired ? <p role="alert" className="rounded border border-warning/40 bg-warning/10 p-2">
          Persisted desired contains APN or interval overrides. New cloud drafts omit these fields.
        </p> : null}
      </CardContent>
    </Card>
  );
}

function ConfigSourceComparison({
  controller,
  draftPreview,
  previewRequestedAt,
}: {
  controller: ControllerDetail;
  draftPreview: ControllerCompilePreview | undefined;
  previewRequestedAt: number;
}): JSX.Element {
  const draft: ConfigSnapshot | null = previewConfigSnapshot(draftPreview);
  const desired: ConfigSnapshot | null = controller.desired_config ?? null;
  const reportedUnavailable: boolean = controller.reported_config == null
    && (controller.reported_config_id !== null || controller.reported_state !== null);
  const desiredUnavailable: boolean = desired === null && controller.desired_config_id !== null;
  return (
    <>
      <div className="overflow-x-auto rounded-md border border-border">
        <table aria-label="Configuration source comparison" className="w-full min-w-[740px] text-left text-xs">
          <thead className="bg-muted/40">
            <tr className="align-top">
              <th scope="col" className="p-3">Setting</th>
              <th scope="col" className="w-[28%] space-y-1 p-3">
                <div className="text-sm">Last device report</div>
                <div>Applied ID: {controller.reported_config_id ?? "unknown"}</div>
                <div>Report received at: {controller.reported_at ? formatTimestamp(controller.reported_at) : "unknown"}</div>
              </th>
              <th scope="col" className="w-[28%] space-y-1 p-3">
                <div className="text-sm">Persisted cloud desired</div>
                <div>Desired ID: {controller.desired_config_id ?? "none"}</div>
                <div>Persisted: {controller.desired_updated_at ? formatTimestamp(controller.desired_updated_at) : "unknown"}</div>
                <div>Publication: {controller.desired_publish_status ?? "unknown"}</div>
                <div>Published: {controller.desired_published_at ? formatTimestamp(controller.desired_published_at) : "unknown"}</div>
              </th>
              <th scope="col" className="w-[28%] space-y-1 p-3">
                <div className="text-sm">Draft next config (not queued)</div>
                <div>{draftPreview ? `Next ID: ${draftPreview.config_id}` : "Preview required"}</div>
                {draftPreview ? <div>Preview requested: {formatTimestamp(new Date(previewRequestedAt).toISOString())}</div> : null}
              </th>
            </tr>
          </thead>
          <tbody>
            {configComparisonFields.map((field) => (
              <tr key={field.label} className="border-t border-border align-top">
                <th scope="row" className="p-3 font-medium">{field.label}</th>
                <td className="whitespace-pre-line break-words p-3">{reportedUnavailable ? "Snapshot unavailable" : field.value(controller.reported_config ?? {}) ?? "Not reported"}</td>
                <td className="whitespace-pre-line break-words p-3">{desiredUnavailable ? "Snapshot unavailable" : field.value(desired ?? {}) ?? "Not sent"}</td>
                <td className="whitespace-pre-line break-words p-3">{field.draftPolicy ?? (draft ? field.value(draft) ?? "Not sent" : "Preview required")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ConfigurationDeliveryFlow({ controller }: { controller: ControllerDetail }): JSX.Element {
  const progress: ConfigDeliveryProgress = configDeliveryProgress(controller);
  const activeStep: number | null = progress.phase === "unknown" ? null
    : progress.phase === "draft" ? 0
    : progress.phase === "queued" ? 1
    : progress.phase === "published" || progress.phase === "failed" ? 2 : 3;
  const steps: readonly string[] = ["Draft", "Cloud queue", "Cloud publication", "Device confirmation"];
  return (
    <div className="flex flex-wrap items-center justify-between gap-1 rounded-md border border-border p-2">
      <ol aria-label="Configuration delivery flow" className="flex flex-wrap items-center gap-2">
        {steps.map((step, index) => (
          <li key={step} className="flex items-center gap-2" aria-current={index === activeStep ? "step" : undefined}>
            {index ? <span aria-hidden="true" className="text-muted-foreground">→</span> : null}
            <Badge variant={index === activeStep ? progress.variant : "outline"}
              className={index === activeStep && progress.phase === "confirmed" ? "bg-emerald-600 text-white" : undefined}>{step}</Badge>
          </li>
        ))}
      </ol>
      <HelpTooltip label="About configuration delivery">
        Published means the cloud publication completed. It does not confirm MQTT delivery.
        Device confirmation uses the reported applied ID and state, not payload equality.
      </HelpTooltip>
    </div>
  );
}

function ControllerConfigWorkspace({
  controller,
  deviceId,
  settingsDraft,
  editor,
  meters,
}: {
  controller: ControllerDetail;
  deviceId: string;
  settingsDraft: SettingsDraft;
  editor: ReactNode;
  meters: ReactNode;
}): JSX.Element {
  const sourceSignature: string = JSON.stringify({
    device_id: controller.device_id,
    desired_config_id: controller.desired_config_id,
    reported_config_id: controller.reported_config_id,
    meters: controller.meters,
  });
  const draftPreviewMutation = useMutation({
    mutationFn: (draft: SettingsDraft) => api.controllerCompilePreviewDraft(deviceId, draft),
    onMutate: (): { sourceSignature: string } => ({ sourceSignature }),
  });
  const previewQuery = useQuery({
    queryKey: ["controller-config-preview", deviceId],
    queryFn: () => api.controllerCompilePreview(deviceId),
    enabled: Boolean(deviceId),
  });
  const preview = previewQuery.data;
  const [comparisonOpen, setComparisonOpen] = useState<boolean>(false);
  const draftPreview: ControllerCompilePreview | undefined =
    draftPreviewMutation.context?.sourceSignature === sourceSignature
    && JSON.stringify(draftPreviewMutation.variables) === JSON.stringify(settingsDraft)
      ? draftPreviewMutation.data
      : undefined;

  return (
    <section aria-label="Configuration workspace" className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[15rem_minmax(0,1fr)]">
      <div data-config-panels className="contents min-w-0 xl:order-2 xl:col-start-2 xl:row-start-1 xl:grid xl:grid-cols-1 xl:gap-4 2xl:grid-cols-2">
        <div data-config-flow className="order-0 min-w-0 xl:col-start-1 xl:row-start-1 2xl:[grid-column:1/3]">
          <ConfigurationDeliveryFlow controller={controller} />
        </div>
        <section aria-label="Editable cloud draft" className="order-1 min-w-0 rounded-xl border-primary/30 bg-primary/5 xl:order-2 xl:col-start-1 xl:row-start-2 2xl:row-start-2">
          {editor}
        </section>
        <section aria-label="Read-only configuration sources" className="order-3 min-w-0 xl:col-start-1 xl:row-start-3 2xl:col-start-2 2xl:row-start-2">
          <div className="grid h-full grid-cols-1 gap-3 sm:grid-cols-2">
            <ConfigSnapshotPanel controller={controller} source="device" />
            <ConfigSnapshotPanel controller={controller} source="cloud" />
          </div>
        </section>
        <section aria-label="Draft next config" className="order-4 h-full min-w-0 space-y-2 rounded-lg border border-primary/30 bg-card p-3 xl:col-start-1 xl:row-start-4 2xl:row-start-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <h4 className="text-sm font-semibold">Draft next config (not queued)</h4>
              <HelpTooltip label="About draft previews">
                Compare config compiled from current saved settings with this unsaved settings draft.
                Neither preview is the persisted desired payload. A preview does not save or queue a configuration.
              </HelpTooltip>
            </div>
            <Button variant="outline" size="sm" disabled={draftPreviewMutation.isPending}
              onClick={() => draftPreviewMutation.mutate(settingsDraft)}>
              {draftPreviewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Preview changes
            </Button>
          </div>
          {draftPreviewMutation.error ? <p role="alert" className="text-sm text-destructive">{draftPreviewMutation.error.message}</p> : null}
          {draftPreview ? <ConfigPreviewDetails preview={draftPreview} /> : <p className="text-xs text-muted-foreground">Preview required</p>}
          <details className="border-t border-border pt-2">
            <summary className="cursor-pointer text-xs font-medium">Saved settings preview (not queued)</summary>
            <div className="mt-2 space-y-2">
              {previewQuery.isLoading ? <Skeleton className="h-10 w-full" /> : preview ? <ConfigPreviewDetails preview={preview} /> : null}
            </div>
          </details>
          {previewQuery.error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {previewQuery.error instanceof Error
                ? previewQuery.error.message
                : "Failed to load the config preview"}
            </div>
          ) : null}
        </section>
        <section aria-label="Device-local settings" className="order-5 h-full min-w-0 xl:col-start-1 xl:row-start-5 2xl:col-start-2 2xl:row-start-3">
          <DeviceSettingsCard controller={controller} />
        </section>
        <details className="order-6 min-w-0 rounded-lg border border-border bg-card p-3 xl:col-start-1 xl:row-start-6 2xl:[grid-column:1/3] 2xl:row-start-4"
          onToggle={(event) => setComparisonOpen(event.currentTarget.open)}>
          <summary className="cursor-pointer text-sm font-medium">Show detailed source comparison</summary>
          {comparisonOpen ? <div data-config-comparison-scroll className="mt-3 max-h-72 overflow-auto"><ConfigSourceComparison controller={controller}
            draftPreview={draftPreview} previewRequestedAt={draftPreviewMutation.submittedAt} /></div> : null}
        </details>
      </div>
      <aside aria-label="Controller meters" className="order-2 min-w-0 self-start xl:order-1 xl:sticky xl:top-24 xl:col-start-1 xl:row-start-1">
        {meters}
      </aside>
    </section>
  );
}

function ConfigPreviewDetails({ preview }: { preview: ControllerCompilePreview }): JSX.Element {
  return (
    <>
      <div className="flex flex-wrap gap-2 text-sm">
        <Badge variant="outline">Next config ID (not queued): {preview.config_id}</Badge>
        <Badge variant="outline">Payload: {preview.payload_size} bytes</Badge>
      </div>
      <details className="rounded-md bg-muted p-3 text-xs">
        <summary className="cursor-pointer font-medium">Show compiled config JSON</summary>
        <pre aria-label="Compiled config JSON" className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words leading-relaxed">
          {JSON.stringify(preview.device_shape, null, 2)}
        </pre>
      </details>
    </>
  );
}

function ControllerSettingsCard({
  draft,
  setDraft,
  canEdit,
  saving,
  onSave,
}: {
  draft: SettingsDraft;
  setDraft: (draft: SettingsDraft) => void;
  canEdit: boolean;
  saving: boolean;
  onSave: () => void;
}): JSX.Element {
  return (
    <Card className="h-full border-primary/30 bg-primary/5">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 p-3">
        <div className="flex items-center gap-1">
          <CardTitle>Editable cloud draft</CardTitle>
          <HelpTooltip label="About saving cloud settings">
            Save and queue saves cloud settings and uses saved meter assignments.
            Queuing does not confirm publication or device application.
          </HelpTooltip>
        </div>
        {canEdit ? (
          <Button disabled={saving} onClick={onSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save and queue config
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2">
          <DraftField
            label="Display name"
            help="A readable controller label, for example Idi-Araba Controller. This does not change the device ID or serial."
            value={draft.display_name}
            disabled={!canEdit}
            onChange={(value) => setDraft({ ...draft, display_name: value })}
          />
          <DraftField
            label="Site code (optional)"
            help="Use your existing installation/site code; leave blank if none. Sent to the controller as site_id. This does not assign a substation or feeder."
            value={draft.site_id}
            disabled={!canEdit}
            onChange={(value) => setDraft({ ...draft, site_id: value })}
          />
          <DraftField
            label="NTP server"
            value={draft.ntp_server}
            disabled={!canEdit}
            onChange={(value) => setDraft({ ...draft, ntp_server: value })}
          />
          <DraftField
            label="Berry script path"
            help="Leave empty to use the built-in script. Use an installed /sd/berry/*.bec file for a custom script."
            value={draft.script_path}
            disabled={!canEdit}
            onChange={(value) => setDraft({ ...draft, script_path: value })}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function DeviceSettingsCard({ controller }: { controller: ControllerDetail }): JSX.Element {
  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3">
        <CardTitle className="text-sm">Device-local settings (read-only)</CardTitle>
        <HelpTooltip label="About device-local settings">
          <span className="block">APNs are read-only. Unknown means the device did not report the value.</span>
          <span className="mt-2 block">Last reported values. Local changes appear after the device sends a new config report.</span>
          <span className="mt-2 block">Firmware default: 15 seconds. Cloud configuration does not send intervals.</span>
          <span className="mt-2 block">Legacy script interval keys do not show the firmware polling interval. These defaults are not device readback.</span>
        </HelpTooltip>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="primary-apn">Primary APN</Label>
            <Input id="primary-apn" disabled readOnly
              value={controller.reported_cellular_apn ?? "Unknown (not reported)"} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="secondary-apn">Secondary APN</Label>
            <Input id="secondary-apn" disabled readOnly
              value={controller.reported_cellular_sim2_apn ?? "Unknown (not reported)"} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="poll-interval-ms">Poll interval ms</Label>
            <Input id="poll-interval-ms" type="number" disabled readOnly
              value={FIRMWARE_POLL_INTERVAL_MS} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sentinel-poll-ms">Sentinel poll ms</Label>
            <Input id="sentinel-poll-ms" type="number" disabled readOnly
              value={FIRMWARE_POLL_INTERVAL_MS} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MeterDialog({
  draft,
  feeders,
  submitting,
  onOpenChange,
  setDraft,
  onSubmit,
}: {
  draft: MeterDraft | null;
  feeders: Feeder[];
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  setDraft: (draft: MeterDraft | null) => void;
  onSubmit: () => void;
}): JSX.Element {
  const dialogDraft = useDialogDraft();
  useEffect(() => {
    if (draft === null) dialogDraft.reset();
  }, [dialogDraft, draft]);
  return (
    <Dialog open={draft !== null} onOpenChange={(open) => {
      if (!open && dialogDraft.canClose(submitting)) onOpenChange(false);
    }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        onChangeCapture={dialogDraft.onChangeCapture} onClickCapture={dialogDraft.onClickCapture} onKeyDownCapture={dialogDraft.onKeyDownCapture}>
        {draft ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {draft.controller_meter_id === null ? "Add meter" : "Edit meter"}
              </DialogTitle>
              <DialogDescription>
                A meter must use exactly one source: feeder template or custom CT/VT values.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <DraftField
                label="Slot"
                type="number"
                value={draft.slot}
                onChange={(value) => setDraft({ ...draft, slot: value })}
              />
              <DraftField
                label="Modbus ID"
                type="number"
                value={draft.modbus_id}
                onChange={(value) => setDraft({ ...draft, modbus_id: value })}
              />
              <DraftField
                label="Label"
                value={draft.label}
                onChange={(value) => setDraft({ ...draft, label: value })}
              />
              <div className="space-y-2">
                <Label>Enabled</Label>
                <Select
                  value={draft.enabled ? "true" : "false"}
                  onValueChange={(value) => setDraft({ ...draft, enabled: value === "true" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Enabled, compile into desired payload</SelectItem>
                    <SelectItem value="false">Disabled, show but do not compile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Source</Label>
                <Select
                  value={draft.source_mode}
                  onValueChange={(value) =>
                    setDraft({ ...draft, source_mode: value as "feeder" | "custom" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feeder">Feeder template</SelectItem>
                    <SelectItem value="custom">Custom CT/VT values</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {draft.source_mode === "feeder" ? (
                <div className="space-y-2">
                  <Label htmlFor="meter-feeder">Feeder</Label>
                  <SearchableSelector
                    id="meter-feeder"
                    label="Feeder"
                    size="form"
                    value={draft.feeder_id}
                    onChange={(value) => setDraft({ ...draft, feeder_id: value })}
                    placeholder="Select feeder"
                    options={feeders.map(feederPickerOption)}
                  />
                </div>
              ) : (
                <>
                  <DraftField
                    label="CT primary"
                    type="number"
                    value={draft.custom_ct_primary}
                    onChange={(value) => setDraft({ ...draft, custom_ct_primary: value })}
                  />
                  <DraftField
                    label="CT secondary"
                    type="number"
                    value={draft.custom_ct_secondary}
                    onChange={(value) => setDraft({ ...draft, custom_ct_secondary: value })}
                  />
                  <DraftField
                    label="VT primary"
                    type="number"
                    value={draft.custom_vt_primary}
                    onChange={(value) => setDraft({ ...draft, custom_vt_primary: value })}
                  />
                  <DraftField
                    label="VT secondary"
                    type="number"
                    value={draft.custom_vt_secondary}
                    onChange={(value) => setDraft({ ...draft, custom_vt_secondary: value })}
                  />
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { if (dialogDraft.canClose(submitting)) onOpenChange(false); }}>
                Cancel
              </Button>
              <Button disabled={submitting} onClick={onSubmit}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save meter
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DraftField({
  label,
  help,
  value,
  onChange,
  type = "text",
  disabled = false,
}: {
  label: string;
  help?: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number";
  disabled?: boolean;
}): JSX.Element {
  const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={id}>{label}</Label>
        {help ? <HelpTooltip label={`About ${label}`}>{help}</HelpTooltip> : null}
      </div>
      <Input
        id={id}
        type={type}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function settingsDraftFromController(controller: ControllerDetail | null): SettingsDraft {
  return {
    display_name: controller?.display_name ?? "",
    site_id: controller?.site_id ?? "",
    ntp_server: controller?.ntp_server ?? DEFAULT_NTP_SERVER,
    script_path: controller?.script_path ?? "",
  };
}

function draftFromMeter(meter: ControllerMeter): MeterDraft {
  return {
    controller_meter_id: meter.controller_meter_id,
    slot: String(meter.slot),
    modbus_id: String(meter.modbus_id),
    enabled: meter.enabled,
    label: meter.label,
    source_mode: meter.source_mode,
    feeder_id: meter.feeder_id === null ? "" : String(meter.feeder_id),
    custom_ct_primary: String(meter.custom_ct_primary ?? ""),
    custom_ct_secondary: String(meter.custom_ct_secondary ?? ""),
    custom_vt_primary: String(meter.custom_vt_primary ?? ""),
    custom_vt_secondary: String(meter.custom_vt_secondary ?? ""),
  };
}

function meterRequestFromDraft(draft: MeterDraft): ControllerMeterUpsertRequest {
  const slot = parsePositiveInteger(draft.slot, "slot");
  const modbusId = parsePositiveInteger(draft.modbus_id, "modbus ID");
  if (modbusId > 247) throw new Error("modbus ID must be between 1 and 247");
  if (draft.source_mode === "feeder") {
    return {
      slot,
      modbus_id: modbusId,
      enabled: draft.enabled,
      label: draft.label,
      source_mode: "feeder",
      feeder_id: parsePositiveInteger(draft.feeder_id, "feeder"),
      custom_ct_primary: null,
      custom_ct_secondary: null,
      custom_vt_primary: null,
      custom_vt_secondary: null,
    };
  }
  return {
    slot,
    modbus_id: modbusId,
    enabled: draft.enabled,
    label: draft.label,
    source_mode: "custom",
    feeder_id: null,
    custom_ct_primary: parsePositiveInteger(draft.custom_ct_primary, "CT primary"),
    custom_ct_secondary: parsePositiveInteger(draft.custom_ct_secondary, "CT secondary"),
    custom_vt_primary: parsePositiveInteger(draft.custom_vt_primary, "VT primary"),
    custom_vt_secondary: parsePositiveInteger(draft.custom_vt_secondary, "VT secondary"),
  };
}

function parsePositiveInteger(value: string, label: string): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    throw new Error(`${label} must be a positive integer`);
  }
  return parsed;
}

function meterLabel(controller: ControllerDetail, meterId: string): string {
  const meter = controller.meters.find((candidate) => String(candidate.modbus_id) === meterId);
  return meter?.label ? `${meter.label} (${meterId})` : `Meter ${meterId}`;
}

function feederName(feeders: Feeder[], feederId: number): string {
  return feeders.find((feeder) => feeder.feeder_id === feederId)?.feeder_name ?? String(feederId);
}

function nextSlot(controller: ControllerDetail): number {
  return controller.meters.reduce((max, meter) => Math.max(max, meter.slot), 0) + 1;
}

function meterCtPrimary(meter: ControllerMeter): string {
  return String(meter.resolved_ct_primary ?? meter.custom_ct_primary ?? "--");
}

function meterCtSecondary(meter: ControllerMeter): string {
  return String(meter.resolved_ct_secondary ?? meter.custom_ct_secondary ?? "--");
}

function meterVtPrimary(meter: ControllerMeter): string {
  return String(meter.resolved_vt_primary ?? meter.custom_vt_primary ?? "--");
}

function meterVtSecondary(meter: ControllerMeter): string {
  return String(meter.resolved_vt_secondary ?? meter.custom_vt_secondary ?? "--");
}
